<script setup lang="ts">
  import { ref, computed, watch, onMounted, nextTick } from 'vue';
  import { useSocketIO, type SocketEvent } from '@/shared/lib';
  import api from '@/shared/api';
  import {
    Radio,
    Trash2,
    Plug,
    Unplug,
    ChevronDown,
    ChevronRight,
    Zap,
    Clock,
    Activity,
    Send,
  } from 'lucide-vue-next';

  const { status, events, errorMessage, connect, disconnect, clearEvents } = useSocketIO();
  const isSending = ref(false);
  const expandedEventId = ref<number | null>(null);
  const feedContainer = ref<HTMLElement | null>(null);
  const filterText = ref('');

  const filteredEvents = computed(() => {
    if (!filterText.value) return events.value;
    const term = filterText.value.toLowerCase();
    return events.value.filter(
      (e) =>
        e.eventName.toLowerCase().includes(term) ||
        JSON.stringify(e.data).toLowerCase().includes(term)
    );
  });

  const statusConfig = computed(() => {
    const configs = {
      disconnected: { label: 'Desconectado', dotClass: 'bg-zinc-500', ringClass: '' },
      connecting: { label: 'Conectando...', dotClass: 'bg-amber-400', ringClass: 'animate-ping-slow' },
      connected: { label: 'Conectado', dotClass: 'bg-emerald-400', ringClass: 'animate-pulse-glow' },
      error: { label: 'Erro', dotClass: 'bg-red-500', ringClass: 'animate-pulse' },
    };
    return configs[status.value];
  });

  const eventDomainColor = (eventName: string): string => {
    const domain = eventName.split('.')[0];
    const colors: Record<string, string> = {
      revision: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
      document: 'text-violet-400 bg-violet-400/10 border-violet-400/30',
      training: 'text-amber-400 bg-amber-400/10 border-amber-400/30',
      permission: 'text-rose-400 bg-rose-400/10 border-rose-400/30',
      branch: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
      folder: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
      record: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/30',
      user: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
      notice: 'text-teal-400 bg-teal-400/10 border-teal-400/30',
    };
    return colors[domain] ?? 'text-zinc-400 bg-zinc-400/10 border-zinc-400/30';
  };

  const formatTime = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions & { fractionalSecondDigits?: number } = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    };
    return date.toLocaleTimeString('pt-BR', options);
  };

  const formatJson = (data: unknown): string => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const toggleExpand = (id: number) => {
    expandedEventId.value = expandedEventId.value === id ? null : id;
  };

  const getEventMessage = (event: SocketEvent): string => {
    if (typeof event.data === 'object' && event.data !== null && 'message' in event.data) {
      return (event.data as { message: string }).message;
    }
    return '';
  };

  const handleToggleConnection = () => {
    if (status.value === 'connected' || status.value === 'connecting') {
      disconnect();
    } else {
      connect();
    }
  };

  const sendTestEvent = async () => {
    isSending.value = true;
    try {
      await api.post('/sandbox/test-event');
    } catch {
      // Event emission errors are visible in the feed itself
    } finally {
      isSending.value = false;
    }
  };

  onMounted(() => {
    connect();
  });

  watch(() => events.value.length, () => {
    if (feedContainer.value) {
      nextTick(() => {
        feedContainer.value!.scrollTop = 0;
      });
    }
  });
</script>

<template>
  <div class="sandbox-console min-h-full bg-zinc-950 p-6 font-mono">
    <!-- Header -->
    <div class="mb-6 flex items-start justify-between">
      <div>
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <Radio class="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h1 class="text-lg font-semibold tracking-tight text-zinc-100">
              WebSocket Monitor
            </h1>
            <p class="text-xs text-zinc-500">
              Sandbox de depuração · Socket.io
            </p>
          </div>
        </div>
      </div>

      <!-- Status pill -->
      <div class="flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2">
        <span class="relative flex h-2.5 w-2.5">
          <span
            v-if="statusConfig.ringClass"
            :class="[statusConfig.dotClass, statusConfig.ringClass, 'absolute inline-flex h-full w-full rounded-full opacity-50']"
          />
          <span :class="[statusConfig.dotClass, 'relative inline-flex h-2.5 w-2.5 rounded-full']" />
        </span>
        <span class="text-xs font-medium text-zinc-300">{{ statusConfig.label }}</span>
      </div>
    </div>

    <!-- Error banner -->
    <div
      v-if="errorMessage"
      class="mb-4 rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-400"
    >
      <span class="font-semibold">Erro:</span> {{ errorMessage }}
    </div>

    <!-- Stats bar -->
    <div class="mb-4 grid grid-cols-3 gap-3">
      <div class="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div class="flex items-center gap-2 text-xs text-zinc-500">
          <Zap class="h-3.5 w-3.5" />
          Total de eventos
        </div>
        <div class="mt-1 text-2xl font-bold tabular-nums text-zinc-100">
          {{ events.length }}
        </div>
      </div>
      <div class="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div class="flex items-center gap-2 text-xs text-zinc-500">
          <Activity class="h-3.5 w-3.5" />
          Filtrados
        </div>
        <div class="mt-1 text-2xl font-bold tabular-nums text-zinc-100">
          {{ filteredEvents.length }}
        </div>
      </div>
      <div class="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div class="flex items-center gap-2 text-xs text-zinc-500">
          <Clock class="h-3.5 w-3.5" />
          Último evento
        </div>
        <div class="mt-1 text-sm font-bold tabular-nums text-zinc-100">
          {{ events.length > 0 ? formatTime(events[0].receivedAt) : '—' }}
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="mb-4 flex items-center gap-2">
      <button
        :class="[
          'flex items-center gap-2 rounded-lg border px-4 py-2 text-xs font-medium transition-all duration-200',
          status === 'connected' || status === 'connecting'
            ? 'border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20'
            : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
        ]"
        @click="handleToggleConnection"
      >
        <Unplug
          v-if="status === 'connected' || status === 'connecting'"
          class="h-3.5 w-3.5"
        />
        <Plug
          v-else
          class="h-3.5 w-3.5"
        />
        {{ status === 'connected' || status === 'connecting' ? 'Desconectar' : 'Conectar' }}
      </button>

      <button
        class="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-xs font-medium text-zinc-400 transition-all duration-200 hover:bg-zinc-700 hover:text-zinc-300"
        :disabled="events.length === 0"
        @click="clearEvents"
      >
        <Trash2 class="h-3.5 w-3.5" />
        Limpar
      </button>

      <button
        class="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-medium text-cyan-400 transition-all duration-200 hover:bg-cyan-500/20 disabled:opacity-40"
        :disabled="isSending || status !== 'connected'"
        @click="sendTestEvent"
      >
        <Send class="h-3.5 w-3.5" />
        Enviar evento teste
      </button>

      <div class="flex-1" />

      <input
        v-model="filterText"
        type="text"
        placeholder="Filtrar eventos..."
        class="w-64 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300 placeholder-zinc-600 outline-none transition-colors focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
      >
    </div>

    <!-- Event feed -->
    <div
      ref="feedContainer"
      class="rounded-lg border border-zinc-800 bg-zinc-900/30"
      :class="filteredEvents.length > 0 ? 'max-h-[calc(100vh-26rem)] overflow-y-auto' : ''"
    >
      <!-- Empty state -->
      <div
        v-if="filteredEvents.length === 0"
        class="flex flex-col items-center justify-center py-20 text-zinc-600"
      >
        <Radio class="mb-3 h-8 w-8 opacity-30" />
        <p class="text-sm">
          {{ events.length === 0 ? 'Aguardando eventos...' : 'Nenhum evento corresponde ao filtro' }}
        </p>
        <p
          v-if="status !== 'connected' && events.length === 0"
          class="mt-1 text-xs text-zinc-700"
        >
          Conecte-se ao servidor para começar a receber eventos
        </p>
      </div>

      <!-- Event rows -->
      <div
        v-for="event in filteredEvents"
        :key="event.id"
        class="group border-b border-zinc-800/50 transition-colors last:border-b-0 hover:bg-zinc-800/30"
      >
        <!-- Event summary row -->
        <button
          class="flex w-full items-center gap-3 px-4 py-3 text-left"
          @click="toggleExpand(event.id)"
        >
          <!-- Expand icon -->
          <ChevronRight
            v-if="expandedEventId !== event.id"
            class="h-3.5 w-3.5 shrink-0 text-zinc-600"
          />
          <ChevronDown
            v-else
            class="h-3.5 w-3.5 shrink-0 text-cyan-400"
          />

          <!-- Timestamp -->
          <span class="w-24 shrink-0 text-xs tabular-nums text-zinc-500">
            {{ formatTime(event.receivedAt) }}
          </span>

          <!-- Event name badge -->
          <span
            :class="[
              eventDomainColor(event.eventName),
              'inline-flex shrink-0 items-center rounded border px-2 py-0.5 text-xs font-medium',
            ]"
          >
            {{ event.eventName }}
          </span>

          <!-- Message preview -->
          <span class="min-w-0 flex-1 truncate text-xs text-zinc-400">
            {{ getEventMessage(event) }}
          </span>
        </button>

        <!-- Expanded detail -->
        <div
          v-if="expandedEventId === event.id"
          class="border-t border-zinc-800/50 bg-zinc-950/50 px-4 py-3"
        >
          <pre class="overflow-x-auto text-xs leading-relaxed text-zinc-400">{{ formatJson(event.data) }}</pre>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="mt-4 flex flex-wrap items-center gap-3 text-[10px] text-zinc-600">
      <span>Domínios:</span>
      <span class="text-cyan-400/70">revision</span>
      <span class="text-violet-400/70">document</span>
      <span class="text-amber-400/70">training</span>
      <span class="text-rose-400/70">permission</span>
      <span class="text-emerald-400/70">branch</span>
      <span class="text-blue-400/70">folder</span>
      <span class="text-indigo-400/70">record</span>
      <span class="text-orange-400/70">user</span>
      <span class="text-teal-400/70">notice</span>
    </div>
  </div>
</template>

<style scoped>
  .sandbox-console {
    font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', ui-monospace, monospace;
  }

  @keyframes pulse-glow {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 0.2; transform: scale(1.8); }
  }

  @keyframes ping-slow {
    0% { transform: scale(1); opacity: 0.5; }
    75%, 100% { transform: scale(2); opacity: 0; }
  }

  :deep(.animate-pulse-glow) {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  :deep(.animate-ping-slow) {
    animation: ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  pre {
    font-family: inherit;
  }
</style>
